import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import Button from "../../../components/base/Button";
import SEO from "../../../components/base/SEO";
import { useState } from "react";

interface AddressOutput {
  district: string;
  subDistrict: string;
  street: string | null;
  streetNo: string | null;
  building: string | null;
  floor: string | null;
  unit: string | null;
  lat: number;
  lng: number;
  confidence: number;
}

interface AddressExample {
  input: string;
  highlight?: string;
  output: AddressOutput;
}

interface AddressCategory {
  category: string;
  description: string;
  items: AddressExample[];
}

const addressExamples: AddressCategory[] = [
  {
    category: "住宅地址",
    description: "私人屋苑、公共屋邨、單幢樓等住宅地址解析",
    items: [
      {
        input: "觀塘巧明街111-113號富利廣場9樓C室",
        output: { district: "觀塘區", subDistrict: "觀塘", street: "巧明街", streetNo: "111-113號", building: "富利廣場", floor: "9樓", unit: "C室", lat: 22.3113, lng: 114.2264, confidence: 0.98 },
      },
      {
        input: "將軍澳唐賢街9號將軍澳中心1座28樓A室",
        output: { district: "西貢區", subDistrict: "將軍澳", street: "唐賢街", streetNo: "9號", building: "將軍澳中心1座", floor: "28樓", unit: "A室", lat: 22.3072, lng: 114.2601, confidence: 0.97 },
      },
      {
        input: "大圍美田路1號大圍站上蓋名城2期3座12B",
        output: { district: "沙田區", subDistrict: "大圍", street: "美田路", streetNo: "1號", building: "名城2期3座", floor: "12樓", unit: "B室", lat: 22.3737, lng: 114.1714, confidence: 0.95 },
      },
      {
        input: "荃灣西樓角路1-17號新領域廣場16/F",
        highlight: "16/F 自動識別為「16樓」",
        output: { district: "荃灣區", subDistrict: "荃灣", street: "西樓角路", streetNo: "1-17號", building: "新領域廣場", floor: "16樓", unit: null, lat: 22.3722, lng: 114.1141, confidence: 0.97 },
      },
      {
        input: "沙田石門安群街1號京瑞廣場2期地下",
        output: { district: "沙田區", subDistrict: "石門", street: "安群街", streetNo: "1號", building: "京瑞廣場2期", floor: "地下", unit: null, lat: 22.3835, lng: 114.2085, confidence: 0.98 },
      },
      {
        input: "天水圍天恩路12號嘉湖山莊美湖居6座5樓F室",
        output: { district: "元朗區", subDistrict: "天水圍", street: "天恩路", streetNo: "12號", building: "嘉湖山莊美湖居6座", floor: "5樓", unit: "F室", lat: 22.4559, lng: 114.0012, confidence: 0.96 },
      },
    ],
  },
  {
    category: "商業地址",
    description: "寫字樓、商場、工業大廈等商業地址解析",
    items: [
      {
        input: "中環皇后大道中15號置地廣場中庭3樓301號舖",
        output: { district: "中西區", subDistrict: "中環", street: "皇后大道中", streetNo: "15號", building: "置地廣場", floor: "3樓", unit: "301號舖", lat: 22.2812, lng: 114.1579, confidence: 0.98 },
      },
      {
        input: "尖沙咀廣東道5號海港城海運大廈地下G12號舖",
        output: { district: "油尖旺區", subDistrict: "尖沙咀", street: "廣東道", streetNo: "5號", building: "海港城海運大廈", floor: "地下", unit: "G12號舖", lat: 22.2955, lng: 114.1685, confidence: 0.97 },
      },
      {
        input: "銅鑼灣軒尼詩道500號希慎廣場8樓",
        output: { district: "灣仔區", subDistrict: "銅鑼灣", street: "軒尼詩道", streetNo: "500號", building: "希慎廣場", floor: "8樓", unit: null, lat: 22.2801, lng: 114.1827, confidence: 0.98 },
      },
      {
        input: "金鐘金鐘道88號太古廣場一座18樓",
        output: { district: "中西區", subDistrict: "金鐘", street: "金鐘道", streetNo: "88號", building: "太古廣場一座", floor: "18樓", unit: null, lat: 22.2773, lng: 114.1651, confidence: 0.99 },
      },
      {
        input: "黃大仙龍翔道136號鑽石山荷李活廣場3樓301號舖",
        output: { district: "黃大仙區", subDistrict: "鑽石山", street: "龍翔道", streetNo: "136號", building: "荷李活廣場", floor: "3樓", unit: "301號舖", lat: 22.3403, lng: 114.2015, confidence: 0.96 },
      },
    ],
  },
  {
    category: "英文地址",
    description: "全英文地址解析，自動比對地政總署雙語地址庫",
    items: [
      {
        input: "Flat A, 12/F, Tower 1, Grand Century Place, 193 Prince Edward Road West, Mongkok",
        output: { district: "油尖旺區", subDistrict: "旺角", street: "太子道西", streetNo: "193號", building: "新世紀廣場1座", floor: "12樓", unit: "A室", lat: 22.3246, lng: 114.1678, confidence: 0.97 },
      },
      {
        input: "Rm 1203, 12/F, Miramar Tower, 132 Nathan Rd, TST",
        highlight: "自動展開簡寫 TST → Tsim Sha Tsui → 尖沙咀",
        output: { district: "油尖旺區", subDistrict: "尖沙咀", street: "彌敦道", streetNo: "132號", building: "美麗華大廈", floor: "12樓", unit: "1203室", lat: 22.2988, lng: 114.1722, confidence: 0.96 },
      },
      {
        input: "Unit B, 5/F, Block 3, Telford Gardens, 33 Wai Yip Street, Kowloon Bay",
        output: { district: "觀塘區", subDistrict: "九龍灣", street: "偉業街", streetNo: "33號", building: "德福花園3座", floor: "5樓", unit: "B室", lat: 22.3231, lng: 114.2138, confidence: 0.97 },
      },
      {
        input: "Shop G05, G/F, Harbour City, 3-27 Canton Road, Tsim Sha Tsui",
        highlight: "G/F 自動識別為「地下」",
        output: { district: "油尖旺區", subDistrict: "尖沙咀", street: "廣東道", streetNo: "3-27號", building: "海港城", floor: "地下", unit: "G05號舖", lat: 22.2955, lng: 114.1685, confidence: 0.98 },
      },
      {
        input: "Suite 1801, 18/F, One Exchange Square, 8 Connaught Place, Central",
        output: { district: "中西區", subDistrict: "中環", street: "康樂廣場", streetNo: "8號", building: "交易廣場第一座", floor: "18樓", unit: "1801室", lat: 22.2844, lng: 114.1563, confidence: 0.97 },
      },
    ],
  },
  {
    category: "中英混合 / 俗名",
    description: "中英文混雜、俗名縮寫——最貼近真實落單場景",
    items: [
      {
        input: "G/F 九龍灣國際展貿中心",
        highlight: "中英混雜：G/F 自動解析為「地下」",
        output: { district: "觀塘區", subDistrict: "九龍灣", street: "展貿徑", streetNo: "1號", building: "九龍灣國際展貿中心", floor: "地下", unit: null, lat: 22.3202, lng: 114.2137, confidence: 0.96 },
      },
      {
        input: "APM 觀塘道",
        highlight: "別名替換：APM → 創紀之城5期",
        output: { district: "觀塘區", subDistrict: "觀塘", street: "觀塘道", streetNo: "418號", building: "創紀之城5期", floor: null, unit: null, lat: 22.3126, lng: 114.2249, confidence: 0.95 },
      },
      {
        input: "IFC 中環",
        highlight: "別名替換：IFC → 國際金融中心",
        output: { district: "中西區", subDistrict: "中環", street: "港景街", streetNo: "1號", building: "國際金融中心", floor: null, unit: null, lat: 22.2854, lng: 114.1580, confidence: 0.94 },
      },
      {
        input: "MK 旺角彌敦道688號旺角中心1期10/F",
        highlight: "展開 MK → 旺角（自動去除重複地名）",
        output: { district: "油尖旺區", subDistrict: "旺角", street: "彌敦道", streetNo: "688號", building: "旺角中心1期", floor: "10樓", unit: null, lat: 22.3193, lng: 114.1694, confidence: 0.95 },
      },
      {
        input: "葵涌葵昌路51-53號荃灣區九龍貿易中心2座15/F",
        highlight: "自動修正錯誤分區：荃灣區 → 葵青區",
        output: { district: "葵青區", subDistrict: "葵涌", street: "葵昌路", streetNo: "51-53號", building: "九龍貿易中心2座", floor: "15樓", unit: null, lat: 22.3568, lng: 114.1287, confidence: 0.93 },
      },
      {
        input: "長沙灣道681號 V Walk L3",
        highlight: "L3 自動識別為商場樓層",
        output: { district: "深水埗區", subDistrict: "長沙灣", street: "長沙灣道", streetNo: "681號", building: "V Walk", floor: "L3", unit: null, lat: 22.3362, lng: 114.1553, confidence: 0.96 },
      },
    ],
  },
  {
    category: "同音字 / 錯字 / 俗稱",
    description: "系統內建同音字映射表及地區容錯規則，自動糾正常見拼寫錯誤",
    items: [
      {
        input: "官塘偉業街33號",
        highlight: "地區容錯：官塘 → 觀塘",
        output: { district: "觀塘區", subDistrict: "觀塘", street: "偉業街", streetNo: "33號", building: null, floor: null, unit: null, lat: 22.3113, lng: 114.2226, confidence: 0.92 },
      },
      {
        input: "彎仔軒尼詩道300號",
        highlight: "同音字糾正：彎仔 → 灣仔",
        output: { district: "灣仔區", subDistrict: "灣仔", street: "軒尼詩道", streetNo: "300號", building: null, floor: null, unit: null, lat: 22.2780, lng: 114.1740, confidence: 0.91 },
      },
      {
        input: "鴨俐洲大街18號",
        highlight: "同音字糾正：鴨俐洲 → 鴨脷洲",
        output: { district: "南區", subDistrict: "鴨脷洲", street: "鴨脷洲大街", streetNo: "18號", building: null, floor: null, unit: null, lat: 22.2411, lng: 114.1530, confidence: 0.90 },
      },
      {
        input: "谷亭街12號",
        highlight: "同音字糾正：谷亭街 → 穀亭街",
        output: { district: "荃灣區", subDistrict: "荃灣", street: "穀亭街", streetNo: "12號", building: null, floor: null, unit: null, lat: 22.3710, lng: 114.1150, confidence: 0.89 },
      },
      {
        input: "南昌站上蓋匯璽",
        highlight: "地區容錯：南昌 → 深水埗",
        output: { district: "深水埗區", subDistrict: "深水埗", street: "深旺道", streetNo: "28號", building: "匯璽", floor: null, unit: null, lat: 22.3263, lng: 114.1535, confidence: 0.93 },
      },
      {
        input: "大角咀晏架街38號",
        highlight: "相鄰地區容忍：大角咀 → 歸類旺角區",
        output: { district: "油尖旺區", subDistrict: "大角咀", street: "晏架街", streetNo: "38號", building: null, floor: null, unit: null, lat: 22.3220, lng: 114.1620, confidence: 0.94 },
      },
      {
        input: "黃竹坑業興街11號",
        highlight: "相鄰地區容忍：黃竹坑 → 歸類香港仔",
        output: { district: "南區", subDistrict: "黃竹坑", street: "業興街", streetNo: "11號", building: null, floor: null, unit: null, lat: 22.2490, lng: 114.1690, confidence: 0.95 },
      },
    ],
  },
  {
    category: "模糊 / 不完整 / 過濾",
    description: "AI 補全不完整地址、識別俗名地標、自動過濾非港地址",
    items: [
      {
        input: "荃灣荃新天地2期",
        highlight: "缺少街道，系統自動補全：楊屋道1號",
        output: { district: "荃灣區", subDistrict: "荃灣", street: "楊屋道", streetNo: "1號", building: "荃新天地2期", floor: null, unit: null, lat: 22.3711, lng: 114.1146, confidence: 0.89 },
      },
      {
        input: "旺角朗豪坊",
        highlight: "只有商場名，系統自動補全完整街道地址",
        output: { district: "油尖旺區", subDistrict: "旺角", street: "亞皆老街", streetNo: "8號", building: "朗豪坊", floor: null, unit: null, lat: 22.3173, lng: 114.1688, confidence: 0.92 },
      },
      {
        input: "沙田火炭禾寮坑路1-5號",
        highlight: "門牌號範圍 1-5 號，系統取中間值匹配",
        output: { district: "沙田區", subDistrict: "火炭", street: "禾寮坑路", streetNo: "3號", building: null, floor: null, unit: null, lat: 22.3955, lng: 114.1860, confidence: 0.93 },
      },
      {
        input: "三十二號彌敦道旺角",
        highlight: "中文數字轉換：三十二號 → 32號",
        output: { district: "油尖旺區", subDistrict: "旺角", street: "彌敦道", streetNo: "32號", building: null, floor: null, unit: null, lat: 22.3154, lng: 114.1700, confidence: 0.91 },
      },
      {
        input: "廣州市天河區體育西路123號",
        highlight: "自動識別為內地地址 → 拒絕解析，返回空結果",
        output: { district: "—", subDistrict: "—", street: null, streetNo: null, building: null, floor: null, unit: null, lat: 0, lng: 0, confidence: 0 },
      },
    ],
  },
  {
    category: "鄉村 / 離島",
    description: "新界鄉村、離島、公共設施等非標準地址",
    items: [
      {
        input: "元朗錦田吉慶圍",
        output: { district: "元朗區", subDistrict: "錦田", street: null, streetNo: null, building: "吉慶圍", floor: null, unit: null, lat: 22.4418, lng: 114.0691, confidence: 0.94 },
      },
      {
        input: "大埔林村許願廣場",
        output: { district: "大埔區", subDistrict: "林村", street: null, streetNo: null, building: "許願廣場", floor: null, unit: null, lat: 22.4653, lng: 114.1431, confidence: 0.93 },
      },
      {
        input: "西貢清水灣道大坳門",
        output: { district: "西貢區", subDistrict: "清水灣", street: "清水灣道", streetNo: null, building: null, floor: null, unit: null, lat: 22.2917, lng: 114.2732, confidence: 0.91 },
      },
      {
        input: "離島長洲東灣道15號",
        output: { district: "離島區", subDistrict: "長洲", street: "東灣道", streetNo: "15號", building: null, floor: null, unit: null, lat: 22.2091, lng: 114.0291, confidence: 0.95 },
      },
      {
        input: "上水石湖墟新豐路18號",
        output: { district: "北區", subDistrict: "上水", street: "新豐路", streetNo: "18號", building: null, floor: null, unit: null, lat: 22.5012, lng: 114.1284, confidence: 0.96 },
      },
    ],
  },
];

const realCapabilities = [
  {
    icon: "ri-translate-2",
    title: "中英雙語解析",
    description: "同時支援純中文、純英文及中英混雜格式輸入。Jieba 中文斷詞引擎搭配地政總署雙語地址詞典，精準拆解複雜格式。",
    example: { before: "G/F 九龍灣國際展貿中心", after: "觀塘區 · 展貿徑1號 · 九龍灣國際展貿中心 · 地下" },
  },
  {
    icon: "ri-eraser-line",
    title: "同音字自動糾正",
    description: "內建同音字映射表，自動識別及修正香港街道及地區名稱中的常見同音異字錯誤。",
    example: { before: "彎仔 · 鴨俐洲 · 谷亭街 · 提頓街", after: "灣仔 · 鴨脷洲 · 穀亭街 · 堤頓街" },
  },
  {
    icon: "ri-link-unlink",
    title: "俗名 / 別名替換",
    description: "內建商業大廈及地標的別名詞典，將客戶常用的縮寫及俗名自動替換為地政總署官方名稱。",
    example: { before: "APM · IFC · YOHO · ICC", after: "創紀之城5期 · 國際金融中心 · Yoho Town · 環球貿易廣場" },
  },
  {
    icon: "ri-compass-3-line",
    title: "108 地區智能歸類",
    description: "覆蓋全港 108 個地區，每個地區設有 acceptDistrict 相鄰容忍機制，即使地址跨越行政分界仍能正確歸類。",
    example: { before: "大角咀 → 旺角區 · 黃竹坑 → 香港仔", after: "南昌 → 深水埗 · 官塘 → 觀塘" },
  },
  {
    icon: "ri-font-size",
    title: "全形轉半形 / 數字轉換",
    description: "自動將全形字符轉為半形，將中文數字轉為阿拉伯數字，處理門牌號範圍取中間值匹配。",
    example: { before: "Ａ · １２３ · 三十二號 · 1-5號", after: "A · 123 · 32號 · 3號（取中間值）" },
  },
  {
    icon: "ri-shield-check-line",
    title: "非港地址過濾",
    description: "自動識別內地省市縣格式及澳門地址關鍵字（氹仔、路環等），拒絕解析非香港地址並返回空結果。",
    example: { before: "廣州市天河區… · 澳門氹仔…", after: "標記為「非港地址」→ 拒絕解析" },
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "多維度評分比對",
    description: "對候選地址進行街道匹配、門牌號範圍、大廈名稱相似度、地區正確性、地理距離加權等多維度評分。",
    example: { before: "多個候選結果", after: "Confidence Score 0-10 → 最高分候選" },
  },
  {
    icon: "ri-scissors-cut-line",
    title: "Jieba 中文斷詞引擎",
    description: "使用 @node-rs/jieba（Rust 加速版）搭配三個自訂詞典：街道索引、屋苑名稱、地政總署地址，將地址拆分為結構化組件。",
    example: { before: "一段連續地址文字", after: "district / street / streetNumber / building / block / floor / unit" },
  },
];

const dataSources = [
  {
    name: "地政總署地址資料庫",
    description: "Land Registry OneLineAddresses — 系統核心數據來源。涵蓋全港已登記街道地址、屋苑、大廈及地段資訊。系統啟動時載入記憶體建立倒排索引。",
    icon: "ri-government-line",
    tag: "核心數據",
  },
  {
    name: "街道索引詞典",
    description: "street-index.json — 全港街道名稱詞典，用於 Jieba 自訂詞典及街道比對，涵蓋中英文官方街名。",
    icon: "ri-road-map-line",
    tag: "自建索引",
  },
  {
    name: "屋苑 / 大廈群組詞典",
    description: "building-groups.json — 香港主要屋苑及大廈名稱群組資料，支持座數、期數、別名等複合結構比對。",
    icon: "ri-building-2-line",
    tag: "自建索引",
  },
  {
    name: "地區對應表 (108 區)",
    description: "district_mapping — 全港 108 個地區的中英文名稱、中心坐標、acceptDistrict 相鄰容忍陣列。",
    icon: "ri-map-2-line",
    tag: "自建索引",
  },
  {
    name: "同音字映射表",
    description: "street-homophone-map.json — 香港街道及地名常見同音異字映射（灣/彎、脷/俐、穀/谷 等），用於斷詞前糾正。",
    icon: "ri-font-size",
    tag: "自建索引",
  },
  {
    name: "別名詞典",
    description: "商業大廈、地標、屋苑的俗名及縮寫映射（APM → 創紀之城5期、IFC → 國際金融中心 等）。",
    icon: "ri-bookmark-line",
    tag: "自建索引",
  },
];

const useCases = [
  {
    icon: "ri-truck-line",
    title: "物流 & 外賣配送",
    description: "訂單地址自動清洗標準化，減少因地址錯誤導致的派送失敗。統一格式後按 108 區自動分組排線。",
    metric: "派送失敗率降低 60%",
  },
  {
    icon: "ri-shopping-cart-line",
    title: "電商結帳",
    description: "客戶填寫地址實時驗證，在下單時即發現問題。自動補全不完整地址，提升結帳轉換率。",
    metric: "退貨率降低 35%",
  },
  {
    icon: "ri-database-2-line",
    title: "CRM 數據清洗",
    description: "批量處理歷史客戶地址，統一格式便於分析。字串相似度比對合併重複客戶記錄。",
    metric: "重複記錄減少 45%",
  },
  {
    icon: "ri-route-line",
    title: "倉儲 & 排線",
    description: "訂單地址標準化後按地區自動分組，配合 AutoTMS 智能排線引擎優化配送路線。",
    metric: "排線效率提升 80%",
  },
  {
    icon: "ri-bank-line",
    title: "金融 KYC / AML",
    description: "客戶開戶時驗證地址真實性，比對地政總署數據庫確認地址是否存在，符合合規要求。",
    metric: "合規效率提升 80%",
  },
  {
    icon: "ri-map-pin-line",
    title: "地理空間分析",
    description: "將文字地址批量轉換為經緯度坐標，用於 GIS 熱力圖、服務範圍計算、門店選址等空間分析。",
    metric: "Geocoding 準確率 96%+",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "客服 / Chatbot",
    description: "即時解析客戶口述或手打的地址，自動糾正錯字及俗名，減少人工確認來回次數。",
    metric: "客服效率提升 50%",
  },
  {
    icon: "ri-home-smile-line",
    title: "地產平台",
    description: "樓盤地址標準化及別名映射，以不同名稱搜尋同一物業（APM = 創紀之城5期），提升搜索體驗。",
    metric: "搜索命中率提升 40%",
  },
];

export default function AddressStandardization() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [expandedExample, setExpandedExample] = useState<number | null>(0);
  const [activeCodeTab, setActiveCodeTab] = useState<"curl" | "js" | "python">("curl");

  const codeExamples = {
    curl: `# 單一地址標準化
curl -X GET \\
  "https://api.autotms.com.hk/cal?address=APM%20觀塘道" \\
  -H "Authorization: Bearer YOUR_API_KEY"

# 含快取的查詢（重複查詢自動命中 Redis）
curl -X GET \\
  "https://api.autotms.com.hk/get?address=觀塘巧明街111號富利廣場9樓C室" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    js: `import { AutoTMSGeocoder } from '@autotms/geocoder-sdk';

const geocoder = new AutoTMSGeocoder({
  apiKey: 'YOUR_API_KEY',
});

// 單一地址解析
const result = await geocoder.parse(
  'APM 觀塘道 12/F'
);
console.log(result);
// {
//   building: "創紀之城5期",
//   street:   "觀塘道",
//   streetNo: "418號",
//   district: "觀塘",
//   floor:    "12樓",
//   lat:      22.3126,
//   lng:      114.2249,
//   score:    8.5
// }

// 批量解析（CSV 模式）
const batchResult = await geocoder.parseBatch([
  '觀塘巧明街111號富利廣場',
  'Rm 1203, Miramar Tower, TST',
  '旺角朗豪坊',
  '官塘偉業街33號',
]);`,
    python: `import requests

API_URL = "https://api.autotms.com.hk"
API_KEY = "YOUR_API_KEY"
headers = {"Authorization": f"Bearer {API_KEY}"}

# 單一地址解析
resp = requests.get(
    f"{API_URL}/cal",
    params={"address": "APM 觀塘道 12/F"},
    headers=headers,
)
result = resp.json()
print(result)
# {'building': '創紀之城5期', 'street': '觀塘道',
#  'streetNo': '418號', 'district': '觀塘', ...}

# 批量解析
import pandas as pd

df = pd.read_csv("orders.csv")
for idx, row in df.iterrows():
    resp = requests.get(
        f"{API_URL}/get",  # /get 端點自動命中 Redis 快取
        params={"address": row["raw_address"]},
        headers=headers,
    )
    df.at[idx, "parsed"] = resp.json()

df.to_csv("orders_parsed.csv", index=False)`,
  };

  const totalExamples = addressExamples.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AutoTMS Geocoder — 香港地址標準化引擎 | 108 區覆蓋 | AUTOCHAIN"
        description="AutoTMS Geocoder 基於地政總署地址資料庫，覆蓋全港 108 個地區。Jieba 斷詞 + 同音字糾正 + 俗名替換 + 多維度評分，將任意格式香港地址轉化為標準化結構。HK$0.1 / 次起。"
        keywords="香港地址標準化, Address Standardization, Geocoder, 地政總署, 地址解析, 同音字糾正, 108區, AutoTMS, 地理編碼, 物流地址"
        canonicalUrl="/tms/address-standardization"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebAPI",
            name: "AutoTMS Geocoder — Hong Kong Address Standardization Engine",
            description: "AI-powered Hong Kong address parsing covering 108 districts, built on Land Registry data",
            url: "https://autochain.com.hk/tms/address-standardization",
            provider: { "@type": "Organization", name: "AUTOCHAIN TECHNOLOGY" },
            offers: { "@type": "Offer", price: "0.1", priceCurrency: "HKD" },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首頁", item: "https://autochain.com.hk/" },
              { "@type": "ListItem", position: 2, name: "TMS 運輸系統", item: "https://autochain.com.hk/tms" },
              { "@type": "ListItem", position: 3, name: "地址標準化引擎", item: "https://autochain.com.hk/tms/address-standardization" },
            ],
          }),
        }}
      />

      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full mb-6 border border-white/10">
                <span className="text-sm font-medium text-blue-200">
                  地政總署數據 · 108 個地區 · Jieba 斷詞引擎
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
                AutoTMS
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  地址標準化引擎
                </span>
              </h1>
              <p className="text-lg md:text-xl mb-8 leading-relaxed text-blue-100/70">
                專為香港物流、外賣、電商場景設計。將客戶手寫、掃描、自填的任意格式地址，
                轉化為標準化結構化地址，並附上地理座標。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  variant="primary"
                  size="lg"
                  className="text-lg px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white"
                  onClick={() => {
                    const msg = encodeURIComponent("您好！我想了解 AutoTMS 地址標準化引擎，希望取得 API Key 試用。");
                    window.open(`https://wa.me/85294252903?text=${msg}`, "_blank");
                  }}
                >
                  免費試用 API
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white/10"
                  onClick={() => document.getElementById("examples")?.scrollIntoView({ behavior: "smooth" })}
                >
                  查看 {totalExamples} 個解析示範
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-blue-200/60">
                <div className="flex items-center"><i className="ri-time-line mr-1.5"></i><span>&lt;200ms（快取 &lt;10ms）</span></div>
                <div className="flex items-center"><i className="ri-map-pin-2-line mr-1.5"></i><span>108 區覆蓋</span></div>
                <div className="flex items-center"><i className="ri-code-s-slash-line mr-1.5"></i><span>REST API</span></div>
              </div>
            </div>

            {/* Hero code preview */}
            <div className="hidden lg:block">
              <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                <div className="flex items-center px-4 py-3 border-b border-white/10">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500/80 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500/80 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500/80 rounded-full"></div>
                  </div>
                  <span className="ml-4 text-xs text-gray-500 font-mono">GET /cal?address=...</span>
                </div>
                <pre className="p-5 text-xs overflow-x-auto">
                  <code className="text-cyan-300">{`{
  "input": "官塘 APM 12/F",
  "processedQuery": "觀塘 創紀之城5期",
  "result": {
    "district":     "觀塘",
    "street":       "觀塘道",
    "streetNumber": "418",
    "building":     "創紀之城5期",
    "floor":        "12",
    "unit":         null,
    "lat":          22.3126,
    "lng":          114.2249,
    "score":        8.5
  },
  "corrections": [
    { "type": "DISTRICT_TYPO",
      "from": "官塘", "to": "觀塘" },
    { "type": "ALIAS_REPLACED",
      "from": "APM", "to": "創紀之城5期" }
  ]
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Banner */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-8 text-white">
              <div className="text-center">
                <span className="text-3xl font-bold">HK$0.1</span>
                <span className="text-cyan-100 ml-1">/ 次</span>
              </div>
              <div className="hidden md:block h-8 w-px bg-white/30"></div>
              <div className="flex gap-6 text-sm">
                <span className="bg-white/20 px-3 py-1 rounded-full">1,000 次 = HK$100</span>
                <span className="bg-white/20 px-3 py-1 rounded-full">10,000 次 = HK$800 <span className="text-yellow-300 font-bold">8折</span></span>
                <span className="bg-white/20 px-3 py-1 rounded-full">100,000 次 = HK$7,000 <span className="text-yellow-300 font-bold">7折</span></span>
              </div>
            </div>
            <span className="text-cyan-100 text-sm">用幾多俾幾多 · 冇月費 · 冇隱藏收費</span>
          </div>
        </div>
      </section>

      {/* Problem Statement — real examples from README */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">THE PROBLEM</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">香港地址有幾混亂？</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              同一個地方，客戶手寫、掃描、口述出嚟嘅地址千奇百怪
            </p>
          </div>

          {/* Real problem table from README */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="grid grid-cols-3 bg-gray-50 text-sm font-semibold text-gray-700 border-b border-gray-200">
                <div className="px-6 py-4">問題類型</div>
                <div className="px-6 py-4">客戶輸入例子</div>
                <div className="px-6 py-4">系統處理方式</div>
              </div>
              {[
                { problem: "中英混雜", example: "G/F 九龍灣國際展貿中心", solution: "雙語並行解析" },
                { problem: "同音異字", example: "彎仔 · 鴨俐洲 · 谷亭街", solution: "同音字映射表自動糾正" },
                { problem: "俗名縮寫", example: "APM · IFC · YOHO", solution: "別名詞典替換為官方名稱" },
                { problem: "簡稱地名", example: "官塘 · 南昌 · 大角咀", solution: "地區容錯規則 + 相鄰容忍" },
                { problem: "內地/澳門地址", example: "廣州市天河區…", solution: "自動過濾，拒絕解析" },
                { problem: "不完整地址", example: "旺角朗豪坊（無街道）", solution: "從數據庫補全完整地址" },
                { problem: "數字格式", example: "三十二號 · Ａ · １２３", solution: "中文數字轉換 + 全形轉半形" },
              ].map((row, i) => (
                <div key={i} className={`grid grid-cols-3 text-sm ${i < 6 ? "border-b border-gray-100" : ""}`}>
                  <div className="px-6 py-3.5 font-medium text-gray-900">{row.problem}</div>
                  <div className="px-6 py-3.5 text-gray-600 font-mono text-xs">{row.example}</div>
                  <div className="px-6 py-3.5 text-blue-600 text-xs">{row.solution}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities — based on real Geocoder features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">CAPABILITIES</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">八大核心解析能力</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              基於 Jieba 斷詞引擎 + 地政總署地址詞典 + 自建容錯規則
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {realCapabilities.map((cap, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-all border border-gray-100 group hover:border-blue-200">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                  <i className={`${cap.icon} text-2xl text-blue-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{cap.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{cap.description}</p>
                <div className="bg-white rounded-lg p-3 border border-gray-200 text-xs font-mono">
                  <div className="text-red-400 line-through mb-1">{cap.example.before}</div>
                  <div className="text-green-600">{cap.example.after}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples */}
      <section id="examples" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">LIVE EXAMPLES</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{totalExamples} 個地址解析實例</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              涵蓋住宅、商業、英文、混合、同音字糾正、鄉村七大類別
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {addressExamples.map((cat, i) => (
              <button
                key={i}
                onClick={() => { setActiveCategory(i); setExpandedExample(0); }}
                className={`px-5 py-2.5 rounded-full font-medium transition-all cursor-pointer text-sm ${
                  activeCategory === i
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {cat.category}
                <span className={`ml-2 text-xs ${activeCategory === i ? "text-blue-200" : "text-gray-400"}`}>
                  ({cat.items.length})
                </span>
              </button>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mb-8">{addressExamples[activeCategory].description}</p>

          <div className="space-y-4">
            {addressExamples[activeCategory].items.map((example, i) => (
              <div
                key={`${activeCategory}-${i}`}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors shadow-sm"
              >
                <button
                  className="w-full px-6 py-5 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedExample(expandedExample === i ? null : i)}
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-blue-600 font-bold text-xs">{i + 1}</span>
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="font-mono text-gray-900 text-sm md:text-base truncate">{example.input}</p>
                      {example.highlight && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center">
                          <i className="ri-magic-line mr-1"></i>{example.highlight}
                        </p>
                      )}
                    </div>
                    {example.output.confidence > 0 && (
                      <div className="hidden md:flex items-center ml-4 flex-shrink-0">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          example.output.confidence >= 0.95 ? "bg-green-100 text-green-700"
                          : example.output.confidence >= 0.90 ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                        }`}>
                          {(example.output.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>
                  <i className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform ml-4 flex-shrink-0 ${expandedExample === i ? "rotate-180" : ""}`}></i>
                </button>

                {expandedExample === i && (
                  <div className="px-6 pb-6">
                    <div className="flex items-center mb-4">
                      <div className="flex-1 h-px bg-gray-100"></div>
                      <div className="px-4 flex items-center text-blue-600">
                        <span className="text-xs font-semibold tracking-wide uppercase">Parsed Result</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-100"></div>
                    </div>

                    {example.output.confidence === 0 ? (
                      <div className="bg-red-50 rounded-xl p-5 border border-red-100 text-center">
                        <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
                        <p className="text-red-600 font-medium">非香港地址 — 拒絕解析</p>
                        <p className="text-red-400 text-sm mt-1">系統自動過濾內地及澳門地址</p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                          {[
                            { label: "district", value: example.output.district },
                            { label: "subDistrict", value: example.output.subDistrict },
                            { label: "street", value: example.output.street },
                            { label: "streetNumber", value: example.output.streetNo },
                          ].map((field, j) => (
                            <div key={j}>
                              <p className="text-[10px] text-gray-400 font-mono mb-0.5">{field.label}</p>
                              <p className="text-gray-900 font-medium text-sm">{field.value || <span className="text-gray-300">null</span>}</p>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {[
                            { label: "building", value: example.output.building },
                            { label: "floor", value: example.output.floor },
                            { label: "unit", value: example.output.unit },
                            { label: "lat, lng", value: `${example.output.lat}, ${example.output.lng}` },
                          ].map((field, j) => (
                            <div key={j}>
                              <p className="text-[10px] text-gray-400 font-mono mb-0.5">{field.label}</p>
                              <p className="text-gray-900 font-medium text-sm">{field.value || <span className="text-gray-300">null</span>}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {example.output.confidence > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700 font-medium">
                          <i className="ri-code-s-slash-line mr-1"></i>查看 JSON Response
                        </summary>
                        <pre className="mt-2 bg-gray-900 text-green-400 rounded-xl p-4 text-[11px] overflow-x-auto">
                          {JSON.stringify({
                            result: {
                              district: example.output.subDistrict,
                              street: example.output.street,
                              streetNumber: example.output.streetNo?.replace("號", ""),
                              building: example.output.building,
                              floor: example.output.floor?.replace("樓", ""),
                              unit: example.output.unit,
                              lat: example.output.lat,
                              lng: example.output.lng,
                              score: +(example.output.confidence * 10).toFixed(1),
                            },
                            ...(example.highlight ? { corrections: [{ note: example.highlight }] } : {}),
                          }, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture — simplified from README */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">ARCHITECTURE</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">地址解析流程</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              四階段串聯處理，從原始輸入到標準化結構化輸出
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01", title: "前處理 QueryHelper",
                desc: "全形轉半形、別名替換（APM → 創紀之城5期）、樓層資訊提取、中文數字轉換（三十二號 → 32號）、門牌號範圍處理。",
                tech: "Regex · 別名詞典 · 同音字映射表",
                icon: "ri-input-method-line", color: "bg-slate-700",
              },
              {
                step: "02", title: "斷詞 JiebaTokenizer",
                desc: "使用 @node-rs/jieba（Rust 加速版）搭配街道索引、屋苑名稱、地政總署地址三個自訂詞典，將地址拆分為結構化 Token。",
                tech: "@node-rs/jieba · 自訂詞典 ×3",
                icon: "ri-scissors-cut-line", color: "bg-blue-600",
              },
              {
                step: "03", title: "搜尋 searchAddressProfile",
                desc: "地區識別（108 區 + acceptDistrict 容忍）→ 街道索引比對 → 屋苑群組比對 → 字串相似度評分 → 地理距離加權排序。",
                tech: "Dice Coefficient · Haversine",
                icon: "ri-search-eye-line", color: "bg-indigo-600",
              },
              {
                step: "04", title: "輸出標準化結果",
                desc: "返回評分最高的候選地址及座標（Lat/Lng），附帶 0-10 信心評分。結果寫入 Redis 快取加速重複查詢。",
                tech: "Redis 快取 · Score 0-10",
                icon: "ri-check-double-line", color: "bg-green-600",
              },
            ].map((step, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow relative">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 ${step.color} rounded-xl flex items-center justify-center`}>
                    <i className={`${step.icon} text-xl text-white`}></i>
                  </div>
                  <span className="text-xs font-mono text-gray-400">STEP {step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{step.desc}</p>
                <div className="bg-white rounded-lg px-3 py-2 border border-gray-200">
                  <span className="text-[11px] font-mono text-blue-600">{step.tech}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Sources */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">DATA SOURCES</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">數據來源</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              以地政總署地址資料庫為核心，搭配多個自建索引及容錯詞典
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {dataSources.map((source, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 hover:shadow-md transition-shadow border border-gray-100">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <i className={`${source.icon} text-xl text-blue-600`}></i>
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    source.tag === "核心數據" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                  }`}>{source.tag}</span>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1.5 leading-snug">{source.name}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{source.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specs */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-cyan-400 tracking-wide uppercase mb-3">TECHNICAL SPECS</p>
            <h2 className="text-3xl font-bold text-white">技術規格</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "單次查詢回應", value: "<200ms", note: "快取命中 <10ms" },
              { label: "覆蓋地區", value: "108 區", note: "港九新界離島" },
              { label: "批量處理", value: "千筆/分鐘", note: "CSV 批量模式" },
              { label: "評分範圍", value: "0-10 分", note: "信心評分" },
              { label: "快取系統", value: "Redis", note: "重複查詢加速" },
              { label: "斷詞引擎", value: "Jieba", note: "@node-rs/jieba (Rust)" },
              { label: "水平擴展", value: "Worker 架構", note: "分散式計算" },
              { label: "資料更新", value: "定期同步", note: "地政總署數據" },
            ].map((spec, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{spec.value}</div>
                <div className="text-sm text-gray-400 mb-1">{spec.label}</div>
                <div className="text-[10px] text-cyan-400 font-mono">{spec.note}</div>
              </div>
            ))}
          </div>
      </div>
      </section>

      {/* API & SDK */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-sm font-semibold text-cyan-400 tracking-wide uppercase mb-3">API & PRICING</p>
              <h2 className="text-4xl font-bold text-white mb-4">REST API 即刻接入</h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                簡單 GET request 即可獲取標準化結果。支援 /cal（直接計算）及 /get（Redis 快取加速）兩個端點。
              </p>
              <div className="space-y-3 mb-10">
                {[
                  "GET /cal — 直接計算，適合批量處理",
                  "GET /get — Redis 快取，重複查詢 <10ms",
                  "GET /geocoder/debug — 完整解析過程除錯",
                  "GET /geocoder/tokenize — 查看斷詞結果",
                  "批量 CSV 模式：萬筆地址分鐘級完成",
                  "Worker 分散式架構，支援水平擴展",
                ].map((feature, i) => (
                  <div key={i} className="flex items-center">
                    <i className="ri-check-line text-cyan-400 mr-3 text-lg"></i>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-xl font-bold text-white">定價方案</h3>
                  <span className="text-xs bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full font-medium">Pay-as-you-go</span>
                </div>
                <div className="flex items-end mb-2">
                  <span className="text-5xl font-bold text-white">HK$0.1</span>
                  <span className="text-gray-400 ml-2 mb-1">/ 每次調用</span>
                </div>
                <p className="text-gray-500 text-sm mb-5">大量使用自動享有折扣</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-gray-400 text-xs">1,000 次</p>
                    <p className="text-white font-bold">HK$100</p>
                    <p className="text-gray-500 text-[10px]">原價</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3 border border-blue-500/30">
                    <p className="text-gray-400 text-xs">10,000 次</p>
                    <p className="text-white font-bold">HK$800</p>
                    <p className="text-yellow-400 text-[10px] font-semibold">8 折優惠</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3 border border-cyan-500/30">
                    <p className="text-gray-400 text-xs">100,000 次</p>
                    <p className="text-white font-bold">HK$7,000</p>
                    <p className="text-yellow-400 text-[10px] font-semibold">7 折優惠</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <p className="text-gray-400 text-xs">大量使用</p>
                    <p className="text-white font-bold">聯絡報價</p>
                    <p className="text-gray-500 text-[10px]">專屬折扣</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Code Tabs */}
            <div>
              <div className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500/80 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500/80 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500/80 rounded-full"></div>
                  </div>
                  <div className="flex gap-1">
                    {(["curl", "js", "python"] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveCodeTab(tab)}
                        className={`px-3 py-1 text-xs rounded-md cursor-pointer transition-colors ${
                          activeCodeTab === tab ? "bg-cyan-500/20 text-cyan-400" : "text-gray-500 hover:text-gray-300"
                        }`}
                      >
                        {tab === "curl" ? "cURL" : tab === "js" ? "JavaScript" : "Python"}
                      </button>
                    ))}
                  </div>
                </div>
                <pre className="p-5 text-[12px] overflow-x-auto min-h-[380px]">
                  <code className="text-green-400">{codeExamples[activeCodeTab]}</code>
                </pre>
              </div>

              <div className="mt-4 bg-gray-800 rounded-2xl overflow-hidden border border-gray-700">
                <div className="flex items-center px-4 py-3 border-b border-gray-700">
                  <span className="text-xs text-gray-400">Response</span>
                  <span className="ml-auto text-[10px] text-green-400 font-mono">200 OK · 45ms</span>
                </div>
                <pre className="p-5 text-[12px] overflow-x-auto">
                  <code className="text-cyan-300">{JSON.stringify({
                    result: {
                      district: "觀塘", street: "觀塘道", streetNumber: "418",
                      building: "創紀之城5期", floor: "12", unit: null,
                      lat: 22.3126, lng: 114.2249, score: 8.5,
                    },
                    corrections: [
                      { type: "ALIAS_REPLACED", from: "APM", to: "創紀之城5期" },
                    ],
                  }, null, 2)}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase mb-3">USE CASES</p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">適用場景</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              物流送貨、外賣配送、電商結帳、CRM 清洗——任何需要處理香港地址嘅場景
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                  <i className={`${uc.icon} text-2xl text-blue-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{uc.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{uc.description}</p>
                <div className="bg-green-50 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full inline-block">{uc.metric}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-950 to-blue-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            準備好接入 AutoTMS 地址標準化引擎？
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            免費試用 · 5 分鐘接入 · 即時開始解析
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              variant="primary"
              size="lg"
              className="text-lg px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={() => {
                const msg = encodeURIComponent("您好！我想試用 AutoTMS 地址標準化引擎，請提供 API Key 及文件。");
                window.open(`https://wa.me/85294252903?text=${msg}`, "_blank");
              }}
            >
              免費取得 API Key
            </Button>
            <button
              onClick={() => {
                const msg = encodeURIComponent("您好！我想了解 AutoTMS 地址標準化引擎的企業方案。");
                window.open(`https://wa.me/85294252903?text=${msg}`, "_blank");
              }}
              className="font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center bg-white/10 text-white border border-white/30 hover:bg-white hover:text-blue-900 px-8 py-4 text-lg"
            >
              <i className="ri-whatsapp-line text-xl mr-2"></i>
              WhatsApp 查詢
            </button>
          </div>
          <div className="text-blue-300/60 text-sm">
            sales@autochain.com.hk · +852 9425 2903
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
